import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectProductGroups } from 'app/pages/Home/slice/selectors';
import { MomentData, MilestonesGridData } from '../types';

/**
 * Custom hook to filter milestones grid data by Product Groups
 * Filters on: moment.metaDetails.productGroup (array of strings)
 * Uses exact matching - if any selected product group matches any product group in the moment's array, include it
 */
export const useFilterByProductGroup = (
  milestonesData: MilestonesGridData | null
): MilestonesGridData | null => {
  // Get selected product groups from Redux state
  const productGroups = useSelector(selectProductGroups);
  const selectedGroups = productGroups?.selectedGroups || [];

  // Memoize the filtered data to avoid unnecessary recalculations
  const filteredData = useMemo(() => {
    // If no data, return null
    if (!milestonesData || !milestonesData.data) {
      return null;
    }

    // If no product groups are selected, return original data
    if (selectedGroups.length === 0) {
      return milestonesData;
    }

    // Filter the data based on selected product groups
    const filtered: MomentData[] = milestonesData.data.filter(moment => {
      const momentProductGroups = moment.metaDetails?.productGroup;

      // If the moment has no product groups, exclude it
      if (
        !momentProductGroups ||
        !Array.isArray(momentProductGroups) ||
        momentProductGroups.length === 0
      ) {
        return false;
      }

      // Check if any of the selected product groups match any product group in the moment's array
      return momentProductGroups.some(group => selectedGroups.includes(group));
    });

    return {
      ...milestonesData,
      data: filtered
    };
  }, [milestonesData, selectedGroups]);

  return filteredData;
};
